import React, { useState, useRef, useEffect } from 'react';
import { View, Text, Pressable, ScrollView, StyleSheet, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import { selectedJobAtom, selectedVersionAtom, selectedVersionLockedAtom, jobUserDataAtom, jobPhaseCodesAtom } from '../store/company';
import { updateUserDataArray, pNum, fNum, deleteCO } from '../db/company';
import XLSX from 'xlsx';
import moment from 'moment';

const COLogUpload = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData] = useAtom(jobUserDataAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [isUploading, setIsUploading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [coRows, setCoRows] = useState([]);
    const [uploadedData, setUploadedData] = useState([]);
    const [disciplines, setDisciplines] = useState({});
    const [showBreakout, setShowBreakout] = useState(false);
    const fileInput = useRef();

    const costTypeDescriptions = {
        "41000": "Revenue",
        "42000": "Revenue",
        "43000": "Revenue",
        "44000": "Revenue",
        "45000": "Revenue",
        "46000": "Revenue",
        "47000": "Revenue",
        "48000": "Revenue",
        "49000": "Revenue",
        "51100": "Regular Time",
        "51200": "Overtime",
        "51300": "Burden",
        "51400": "BYOD",
        "51500": "Per Diem",
        "51600": "Truck Pay",
        "51700": "Welding Rig Pay",
        "51800": "Field Bonus",
        "52100": "Inside Equipment Rental",
        "52200": "Outside Equipment Rental",
        "52300": "Fuel",
        "52400": "Repairs",
        "53100": "Materials",
        "53200": "Mats",
        "53210": "Mats",
        "53300": "Tools & Supplies",
        "53400": "Skids",
        "54100": "Internal Subcontractors",
        "54200": "External Subcontractors",
        "54300": "Hauling and Transportation",
        "55100": "Personal Meals",
        "55200": "Team Meals/Entertain",
        "55300": "Airfare",
        "55400": "Lodging",
        "56100": "Field Office Rent",
        "56200": "Field Office Utility",
        "56300": "Field Office Expense",
        "56400": "Lab Fees & Testing",
        "56500": "Safety & PPE",
        "56600": "Bonds and Insurance Premiums",
        "56700": "Permits",
        "56800": "Insurance Claims",
        "57100": "Taxes",
        "57200": "Accrued Expense",
        "57300": "Warranty Reserve",
        "57900": "Miscellaneous",
        "55900": "Miscellaneous",
    };

    const COLORS = {
        navy: "#173659",
        navyDark: "#0F2A4A",
        headerText: "#FFFFFF",
        subHeader: "#224774",
        grid: "#B7C4D3",
        zebra: "#F6F8FB",
        rowHighlight: "#E5F3EA",
        cellText: "#0E1B2B",
        white: "#FFFFFF",
    };

    const bw = StyleSheet.hairlineWidth;

    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            loadUploadedData();
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    // Load phase -> discipline mapping from Productivity Tracker (same as productivityCombined)
    useEffect(() => {
        const uData = {};
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Productivity Tracker' && d.Forecast_Field_Name === 'discipline') {
                    uData[d?.Forecast_Row_ID] = d.Forecast_Value;
                }
            });
        }
        setDisciplines(uData);
    }, [jobUserData]);

    const loadUploadedData = () => {
        try {
            // Load Change Order data (main rows) — deduplicate same as overviewNew
            const rawCoData = jobUserData.filter(row =>
                (row.Forecast_Tab === "Change Order") &&
                row.BU_ID === selectedJob &&
                row.Version_GUID === selectedVersion
            );
            const grouped = rawCoData.reduce((acc, item) => {
                const key = `${item.Version_GUID}_${item.BU_ID}_${item.Forecast_Tab}_${item.Forecast_Field_Name}_${item.Forecast_Row_ID}`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
            }, {});
            const uploadedCoData = Object.values(grouped).map(group => group.reduce((latest, current) => {
                const ld = new Date(Math.max(new Date(latest.DSS_Created_Date), new Date(latest.DSS_Updated_Datetime || 0)));
                const cd = new Date(Math.max(new Date(current.DSS_Created_Date), new Date(current.DSS_Updated_Datetime || 0)));
                return cd > ld ? current : latest;
            }));

            // Load Change Order Header data to get descriptions
            const headerData = jobUserData.filter(row =>
                (row.Forecast_Tab === "Change Order Header") &&
                row.BU_ID === selectedJob &&
                row.Version_GUID === selectedVersion
            );

            const mapType = {
                "Internal C": "Internal Change",
                "Customer R": "Customer Requested",
                "Unit Adder": "Unit Adder",
                "Bid Miss": "Bid Miss"
            };

            // Create a map of CO ID to description from header data
            // Parse CO IDs as integers to match regardless of leading zeros
            const descriptionMap = {};
            headerData.forEach(header => {
                const coId = header.rowLabel || header.Row_Label || '';
                const description = header.description || header.Description || '';
                header.PO_Number = mapType[header.PO_Number]
                if (coId) {
                    const parsedId = parseInt(coId, 10);
                    if (!isNaN(parsedId)) {
                        descriptionMap[parsedId] = description; 
                    }
                }
            });

            // Add description to each Change Order row
            const enrichedCoData = uploadedCoData.map(row => {
                const coId = row.rowLabel || row.Row_Label || '';
                let matchedDescription = '';
                
                // Try to match using parsed integer
                const parsedId = parseInt(coId, 10);
                if (!isNaN(parsedId) && descriptionMap[parsedId]) {
                    matchedDescription = descriptionMap[parsedId];
                }
                
                return {
                    ...row,
                    description: row.description || matchedDescription || '',
                    Description: row.Description || matchedDescription || ''
                };
            });

            console.log('Loaded uploaded CO data:', enrichedCoData);
            setCoRows(enrichedCoData);
            setUploadedData(enrichedCoData);
        } catch (error) {
            console.error('Error loading uploaded data:', error);
        }
    };

    const fileSelectedHandler = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });

                const { processedData, headerData } = processExcelData(wb);
                console.log('Processed Data:', processedData);
                console.log('Header Data:', headerData);

                if (processedData && processedData.length > 0) {
                    // Check for rows without CO IDs
                    const rowsWithoutIds = processedData//.filter(row => 
                    //    !row.rowLabel && !row.Row_Label
                    //);
                    
                    // if (rowsWithoutIds.length > 0) {
                    //     alert(`Warning: ${rowsWithoutIds.length} row(s) found without CO ID. These rows will be uploaded but may need to be reviewed.`);
                    // }
                    
                    saveUploadedData(processedData);
                } else {
                    console.error('No valid data found in the Excel file');
                }
            } catch (error) {
                console.error('Error processing Excel file:', error);
                alert('Processing Error: Error processing Excel file. Please ensure it\'s a valid Excel format.');
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsBinaryString(file);

        // Clear the input
        e.target.value = '';
    };

    const processExcelData = (workbook) => {
        const sheetNames = workbook.SheetNames;
        let extractedData = [];
        let headerRowsData = [];
        let statusMap = {};
        let rowsToSave = []; // Initialize single array for all database rows
        console.log('sheets', sheetNames)
        
        // Helper function to generate random 8-digit number
        const generateRandomId = () => {
            return Math.floor(10000000 + Math.random() * 90000000).toString();
        };

        // Process header information with comprehensive field mapping
        if (sheetNames.includes('Headers')) {
            const headersSheet = workbook.Sheets['Headers'];
            const sheetData = XLSX.utils.sheet_to_json(headersSheet, { header: 1, raw: true });
            
            console.log('Header Sheet Data:', sheetData);
            if (sheetData && sheetData.length > 0) {
                const headerColumns = sheetData[1] || [];
                const dateFields = ['Initiated', 'Submitted', 'Signed'];
                
                // Helper function to validate checkbox values
                const validateCheckboxValue = (value) => {
                    if (value === true || value === 1 || value === 'TRUE' || value === 'true' || 
                        value === 'Yes' || value === 'yes' || value === 'Y' || value === 'y' ||
                        value === 'X' || value === 'x') {
                        return true;
                    }
                    return false;
                };

                // Helper function to format Excel dates
                const formatExcelDate = (value) => {
                    if (typeof value === 'number' && value > 25000 && value < 100000) {
                        const excelEpoch = new Date(1899, 11, 30);
                        const jsDate = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
                        return jsDate.toLocaleDateString('en-US');
                    }
                    return value;
                };

                headerRowsData = [];
                
                sheetData.slice(2).forEach((row, index) => {
                    if (row.length > 0 && row.some(cell => cell != null && cell !== '')) {
                        const rowData = {};
                        headerColumns.forEach((col, colIndex) => {
                            if (col && row[colIndex] != null) {
                                rowData[col] = row[colIndex];
                            }
                        });

                        // Only process rows that have a CO number (No.)
                        if (rowData['No.'] != null && rowData['No.'] !== '') {
                            const coNumber = rowData['No.'] + '';
                            const rowId = coNumber + '_' + generateRandomId();
                            
                            // Process and format all fields for this CO
                            const processedRowData = { ...rowData };
                            
                            // Format dates
                            dateFields.forEach(field => {
                                if (processedRowData[field]) {
                                    processedRowData[field] = formatExcelDate(processedRowData[field]);
                                }
                            });
                            
                            // Validate and set boolean fields
                            const taskValue = processedRowData['Task'] ? validateCheckboxValue(processedRowData['Task']) : false;
                            const criticalPathValue = processedRowData['Critical Path'] ? validateCheckboxValue(processedRowData['Critical Path']) : false;
                            
                            // Create display row for UI table
                            const headerRow = {
                                'CO ID': coNumber,
                                'Status': processedRowData['Status'] || '',
                                'Description': processedRowData['Description'] || '',
                                'Type': processedRowData['Type'] || '',
                                'Revenue Type': processedRowData['Revenue Type'] || '',
                                'Days Ext': processedRowData['Days Ext'] || '',
                                'Task': taskValue,
                                'Critical Path': criticalPathValue,
                                'Initiated': processedRowData['Initiated'] ? formatExcelDate(processedRowData['Initiated']) : '',
                                'Submitted': processedRowData['Submitted'] ? formatExcelDate(processedRowData['Submitted']) : '',
                                'Signed': processedRowData['Signed'] ? formatExcelDate(processedRowData['Signed']) : '',
                                'Extension': processedRowData['Extension'] || '',
                                'Phase': processedRowData['Phase'] || processedRowData['Cost Code'] || '',
                                'Notes': processedRowData['Notes'] || processedRowData['additionalNotes'] || ''
                            };
                            
                            headerRowsData.push(headerRow);
                            
                            // Create database-formatted consolidated row for each header
                            const consolidatedRow = {
                                userId: '154045',
                                userName: 'Seth Bailey',
                                tab: "Change Order Header",
                                rowId: rowId,
                                version: selectedVersion + '',
                                id: selectedJob,
                                lockedVersion: lockedVersion,
                                fieldName: 'Row_Label',
                                value: coNumber,
                                valueAmount: isNaN(pNum(processedRowData['Extension'])) ? 0 : pNum(processedRowData['Extension']),
                                costCode: processedRowData['Phase'] || processedRowData['Cost Code'] || '',
                                rowLabel: coNumber,
                                description: processedRowData['Description'] || '',
                                status: processedRowData['Status'] || '',
                                notes: processedRowData['Notes'] || processedRowData['additionalNotes'] || '',
                                po: processedRowData['Type'] || '',
                                quantity: processedRowData['Days Ext'] || '',
                                resourceCode: processedRowData['Revenue Type'] || '',
                                active: taskValue,
                                projectStart: processedRowData['Initiated'] ? formatExcelDate(processedRowData['Initiated']) : '',
                                projectEnd: processedRowData['Submitted'] ? formatExcelDate(processedRowData['Submitted']) : '',
                                phaseStart: processedRowData['Signed'] ? formatExcelDate(processedRowData['Signed']) : '',
                                equipmentTier: criticalPathValue,
                            };
                            
                            rowsToSave.push(consolidatedRow);
                            
                            // Create status mapping for backward compatibility
                            const coId = Math.floor(parseFloat(coNumber) || 0).toString();
                            if (headerRow['Status']) {
                                statusMap[coId] = headerRow['Status'];
                            }
                        }
                    }
                });
            }
        }


        if (workbook.Sheets['Upload']) {
            const worksheet = workbook.Sheets['Upload'];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            // Helper function to extract only numbers from a string (e.g., "51100 - Regular Time" -> "51100")
            const extractNumbers = (value) => {
                if (!value) return '';
                const strValue = String(value).trim();
                const match = strValue.match(/^\d+/);
                return match ? match[0] : strValue;
            };

            // Normalize the data by trimming column names
            const normalizedData = jsonData.map(row => {
                const normalizedRow = {};
                Object.keys(row).forEach(key => {
                    const trimmedKey = key.trim();
                    normalizedRow[trimmedKey] = row[key];
                });
                return normalizedRow;
            });

            const desiredColumns = ['CO ID', 'Status', 'Cost Code', 'Cost Type', 'Dollars', 'Hours', 'Resource Code', 'Quantity'];

            extractedData = normalizedData.map((row, index) => {
                const processedRow = {};
                desiredColumns.forEach(col => {
                    if (col === 'Cost Code') {
                        processedRow[col] = row[col] ? String(row[col]).substring(0, 6) : '';
                    } else if (col === 'Cost Type') {
                        // Extract only numbers from Cost Code and Cost Type
                        processedRow[col] = row[col] ? String(row[col]).substring(0, 5) : '';
                    } else if (col === 'CO ID') {
                        // Ensure CO ID is always a string
                        processedRow[col] = row[col] != null ? String(row[col]) : '';
                    } else {
                        processedRow[col] = row[col] ?? '';
                    }
                });
                return processedRow;
            }).filter(row => (row['CO ID'] != null && row['CO ID'] !== '') && (row['Cost Type'] || row['Status']));
        }

        console.log('extractedData', extractedData);

        // Check for rows without CO ID
        const rowsWithoutCoId = extractedData.filter(row => row['CO ID'] == null || row['CO ID'].toString().trim() === '');
        if (rowsWithoutCoId.length > 0) {
            const message = `Warning: Found ${rowsWithoutCoId.length} row(s) in the Upload tab without a CO ID.\n\nRows without CO ID cannot be properly tracked and will cause issues with data integrity.\n\nPlease add a CO ID to all rows before uploading.`;
            
            alert(message);
            throw new Error('Upload cancelled: Rows missing CO ID');
        }

        // Valid status values
        const validStatuses = ['Approved', 'Pending - Performed', 'Pending - Not Performed', 'Not Submitted', 'Denied', 'Passthru'];

        // Check for rows without status
        const rowsWithoutStatus = extractedData.filter(row => !row['Status'] || row['Status'].toString().trim() === '');
        if (rowsWithoutStatus.length > 0) {
            const coIds = [...new Set(rowsWithoutStatus.map(row => row['CO ID']))].join(', ');
            const message = `Warning: Found ${rowsWithoutStatus.length} row(s) in the Upload tab without a Status value.\nCO IDs affected: ${coIds}\n\nDo you want to continue with the upload?`;
            
            if (!confirm(message)) {
                throw new Error('Upload cancelled by user due to missing Status values');
            }
        }

        // Check for invalid status values
        const rowsWithInvalidStatus = extractedData.filter(row => {
            const status = row['Status'] ? row['Status'].toString().trim() : '';
            return status && !validStatuses.includes(status);
        });
        if (rowsWithInvalidStatus.length > 0) {
            const invalidStatuses = rowsWithInvalidStatus.map(row => `CO ID: ${row['CO ID']}, Status: "${row['Status']}"`).join('\n');
            const message = `Warning: Found ${rowsWithInvalidStatus.length} row(s) with invalid Status values.\n\nValid statuses are: ${validStatuses.join(', ')}\n\nInvalid rows:\n${invalidStatuses}\n\nDo you want to continue with the upload?`;
            
            if (!confirm(message)) {
                throw new Error('Upload cancelled by user due to invalid Status values');
            }
        }

        // Convert to database format
        const processedExtractedData = extractedData;
        // rowsToSave already initialized above for header processing

        processedExtractedData.forEach((row, index) => {
            const coId = row['CO ID'] || '';
            const costTypeStr = row['Cost Type'] ? String(row['Cost Type']).trim() : '';
            const costType = costTypeStr ? (Number(costTypeStr) || costTypeStr) : '';
            const resourceCode = row['Resource Code'] || '';
            
            // Determine fieldName based on Cost Type to ensure unique rowId + fieldName combinations
            let fieldName = 'row';
            
            // Revenue fields (41000)
            if (costType == 41000 || costType === '41000') {
                // Use Resource Code to differentiate revenue types
                if (resourceCode.toLowerCase().includes('tm') || resourceCode.toLowerCase().includes('t&m')) {
                    fieldName = 'tAndM';
                } else if (resourceCode.toLowerCase().includes('change') || resourceCode.toLowerCase().includes('co')) {
                    fieldName = 'changeOrders';
                } else if (resourceCode.toLowerCase().includes('reduction') || resourceCode.toLowerCase().includes('ros')) {
                    fieldName = 'reductionOfScope';
                } else if (resourceCode.toLowerCase().includes('unit') || resourceCode.toLowerCase().includes('price')) {
                    fieldName = 'unitPrice';
                } else {
                    fieldName = 'changeOrders'; // Default revenue type
                }
            }
            // Quantity field
            else if (row['Quantity'] && pNum(row['Quantity']) !== 0 && (!row['Dollars'] || pNum(row['Dollars']) === 0) && (!row['Hours'] || pNum(row['Hours']) === 0)) {
                fieldName = 'budgetQty';
            }
            // Labor hours field (51100 with hours but no dollars)
            else if (costType == 51100 && row['Hours'] && pNum(row['Hours']) !== 0) {
                fieldName = 'totalLaborHours';
            }
            // Equipment hours field (52100 with hours)
            else if (costType == 52100 && row['Hours'] && pNum(row['Hours']) !== 0) {
                fieldName = 'totalEquipmentHours';
            }
            // Cost type fields (5xxxx series)
            else if (costTypeStr.length === 5 && costTypeStr.startsWith('5')) {
                fieldName = costTypeStr;
            }
            
            // Create unique rowId based on CO ID, fieldName, and random number
            const uniqueRowId = `${coId}_${fieldName}_${generateRandomId()}`;
            
            const processedDollars = isNaN(pNum(row['Dollars'])) ? 0 : pNum(row['Dollars']);
            const processedHours = isNaN(pNum(row['Hours'])) ? 0 : pNum(row['Hours']);
            const processedQuantity = isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']);
            const status = row['Status'] || '';

            const dbRow = {
                userId: '154045',
                userName: 'Seth Bailey',
                tab: "Change Order",
                rowId: uniqueRowId,
                version: selectedVersion + '',
                id: selectedJob,
                lockedVersion: lockedVersion,
                fieldName: fieldName,
                value: row['Dollars'] || '',
                valueAmount: processedDollars,
                costCode: row['Cost Code'] || '',
                costType: row['Cost Type'] || '',
                rowLabel: row['CO ID'] || '',
                status: status,
                resourceCode: row['Resource Code'] || '',
                hours: processedHours,
                quantity: processedQuantity,
                // Database structure fields
                Cost_Code: row['Cost Code'] ? Number(row['Cost Code']) || row['Cost Code'] : '',
                Cost_Type: costType,
                Forecast_Field_Name: fieldName,
                Forecast_Tab: "Change Order",
                Forecast_Row_ID: uniqueRowId,
                Row_Label: row['CO ID'] || '',
                Status: status,
                Value_Amount: processedDollars,
                Quantity: processedQuantity,
                Hours: processedHours,
                Resource_Code: row['Resource Code'] || ''
            };

            rowsToSave.push(dbRow);
        });

        return {
            processedData: rowsToSave,
            headerData: headerRowsData
        };
    };

    const saveUploadedData = async (processedData) => {
        try {
            // Delete existing CO data before saving new data
            const deleteResult = await deleteCO({
                id: selectedJob,
                forecastGuid: selectedVersion.toString()
            });
            
            console.log('Delete result:', deleteResult);
            console.log('Saving', processedData.length, 'new rows');
            
            await updateUserDataArray(processedData);

            // Filter out Change Order Header rows - only show Change Order rows in the table
            const changeOrderRows = processedData.filter(row => 
                row.tab === "Change Order" || row.Forecast_Tab === "Change Order"
            );
            
            setCoRows(changeOrderRows);
            setUploadedData(changeOrderRows);
        } catch (error) {
            console.error('Error saving uploaded data:', error);
            alert('Save Failed: Error saving uploaded data. Please try again.');
        }
    };

    const exportCOLogsToExcel = async () => {
        const filename = `colog_${selectedJob}.xlsx`;
        setIsExporting(true);
        
        try {
            // Get all Change Order data
            const changeOrderData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            // Get Change Order Header data
            const changeOrderHeaderData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order Header" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            // Transform Change Order data to Upload sheet format
            const uploadData = changeOrderData.map(row => ({
                'CO ID': row.rowLabel || row.Row_Label || '',
                'Status': row.status || row.Status || '',
                'Cost Code': row.costCode || row.Cost_Code || '',
                'Cost Type': row.costType || row.Cost_Type || '',
                'Dollars': row.valueAmount || row.Value_Amount || 0,
                'Hours': row.hours || row.Hours || 0,
                'Resource Code': row.resourceCode || row.Resource_Code || '',
                'Quantity': row.quantity || row.Quantity || 0
            }));

            // Transform Change Order Header data to Headers sheet format
            const headersData = changeOrderHeaderData.map(row => {
                // Format dates to MM/DD/YYYY format
                const formatDate = (dateValue) => {
                    if (!dateValue) return '';
                    const date = moment(dateValue);
                    return date.isValid() ? date.format('MM/DD/YYYY') : '';
                };

                return {
                    'No.': row.rowLabel || row.Row_Label || '',
                    'Type': row.PO_Number || '',
                    'Revenue Type': row.resourceCode || row.Resource_Code || '',
                    'Description': row.description || row.Description || '',
                    'Status': row.status || row.Status || '',
                    'Initiated': formatDate(row.projectStart || row.Project_Start),
                    'Submitted': formatDate(row.projectEnd || row.Project_End),
                    'Signed': formatDate(row.phaseStart || row.Phase_Start),
                    'Revenue': 0,
                    'Cumulative Amount to Date': 0,
                    'Extension': row.valueAmount || row.Value_Amount || 0,
                    'Task': row.active || row.Active || false,
                    'Critical Path': row.equipmentTier || false,
                    'GM%': 0,
                    'Notes': row.notes || row.Notes || ''
                };
            });

            // Sort both datasets by CO ID
            const sortByCoId = (a, b) => {
                const aId = parseFloat(a['No.'] || a['CO ID']) || 0;
                const bId = parseFloat(b['No.'] || b['CO ID']) || 0;
                return aId - bId;
            };

            headersData.sort(sortByCoId);
            uploadData.sort(sortByCoId);

            console.log('Exporting Upload Data:', uploadData);
            console.log('Exporting Headers Data:', headersData);

            // Prepare phase codes for dropdown - only 6-digit numbers
            const phaseCodes = jobPhaseCodes ? Object.keys(jobPhaseCodes).filter(code => /^\d{6}$/.test(code)).sort() : [];
            const phaseCodesWithDescriptions = phaseCodes.map(code => `${code} - ${jobPhaseCodes[code]}`);


            console.log('Phase Codes with Descriptions:', phaseCodesWithDescriptions);
            // Prepare cost types for dropdown - only include 41000 for revenue, then all 5xxxx codes
            const costTypes = Object.keys(costTypeDescriptions)
                .filter(code => code === '41000' || code.startsWith('5'))
                .sort();
            const costTypesWithDescriptions = costTypes.map(code => `${code} - ${costTypeDescriptions[code]}`);

            // Build aggregated logs data for old format sheet
            const logsData = {};
            changeOrderData.forEach((row, index) => {
                const coId = row.rowLabel || row.Row_Label || `__no_id_${index}__`;
                if (!logsData[coId]) {
                    logsData[coId] = {
                        coNo: row.rowLabel || row.Row_Label || '',
                        Row_Label: row.rowLabel || row.Row_Label || '',
                        phase: row.costCode || row.Cost_Code || '',
                        Cost_Code: row.costCode || row.Cost_Code || '',
                        itemDescription: row.description || row.Description || '',
                        Description: row.description || row.Description || '',
                        status: row.status || row.Status || '',
                        Status: row.status || row.Status || ''
                    };
                }
                
                const costType = row.costType || row.Cost_Type || '';
                const fieldName = row.forecastFieldName || row.Forecast_Field_Name || '';
                const valueAmount = parseFloat(row.valueAmount || row.Value_Amount || 0);
                
                // Map revenue fields
                if (fieldName.toLowerCase().includes('unitprice')) {
                    logsData[coId].unitPrice = (logsData[coId].unitPrice || 0) + valueAmount;
                } else if (fieldName.toLowerCase().includes('tandm')) {
                    logsData[coId].tAndM = (logsData[coId].tAndM || 0) + valueAmount;
                } else if (fieldName.toLowerCase().includes('changeorder')) {
                    logsData[coId].changeOrders = (logsData[coId].changeOrders || 0) + valueAmount;
                } else if (fieldName.toLowerCase().includes('reductionofscope')) {
                    logsData[coId].reductionOfScope = (logsData[coId].reductionOfScope || 0) + valueAmount;
                } else if (fieldName === 'budgetQty' || fieldName === 'Quantity') {
                    logsData[coId].budgetQty = (logsData[coId].budgetQty || 0) + valueAmount;
                } else if (fieldName === 'totalLaborHours' || fieldName === 'Hours') {
                    logsData[coId].totalLaborHours = (logsData[coId].totalLaborHours || 0) + valueAmount;
                } else if (fieldName === 'totalEquipmentHours') {
                    logsData[coId].totalEquipmentHours = (logsData[coId].totalEquipmentHours || 0) + valueAmount;
                }
                
                // Map cost type fields (51100-57900)
                if (costType && costType.match(/^5\d{4}$/)) {
                    logsData[coId][costType] = (logsData[coId][costType] || 0) + valueAmount;
                }
            });
            
            // Calculate totals for each CO
            const logs = Object.values(logsData).map(log => ({
                ...log,
                totalRevenue: (log.unitPrice || 0) + (log.tAndM || 0) + (log.changeOrders || 0) + (log.reductionOfScope || 0),
                totalCosts: Object.keys(log)
                    .filter(key => key.match(/^5\d{4}$/))
                    .reduce((sum, key) => sum + (log[key] || 0), 0),
                overhead: 0,
                grossProfit: 0,
                gpPercent: 0
            })).map(log => ({
                ...log,
                grossProfit: log.totalRevenue - log.totalCosts,
                gpPercent: log.totalRevenue > 0 ? ((log.totalRevenue - log.totalCosts) / log.totalRevenue * 100).toFixed(2) : 0
            }));

            const response = await fetch('https://forecastingtestpythonservice.azurewebsites.net/api/download_co_template', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    coRows: uploadData,
                    combinations: headersData,
                    jobId: selectedJob,
                    customerViewRows: headersData,
                    jobData: { BU_Description: selectedJob },
                    phaseCodes: phaseCodesWithDescriptions,
                    costTypes: costTypesWithDescriptions,
                    logs: logs
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            console.log('CO Log Excel file downloaded successfully');
        } catch (error) {
            console.error('Error downloading CO Log Excel file:', error);
            alert('Failed to download CO Log Excel file. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <View style={{ flex: 1, backgroundColor: '#fff' }}>
            <View style={{ backgroundColor: '#fff', paddingLeft: 20, flexDirection: 'row' }}>
                <View style={{ flexDirection: 'row', padding: 5 }}>
                    {/* <Pressable
                        style={{
                            alignItems: 'center',
                            borderColor: '#cfd3d5',
                            borderWidth: 1,
                            borderRadius: 8,
                            justifyContent: 'center',
                            paddingHorizontal: 8,
                            paddingVertical: 12,
                            marginLeft: 10,
                            backgroundColor: isUploading ? '#f5f5f5' : 'transparent',
                            opacity: isUploading ? 0.6 : 1,
                        }}
                        onPress={() => !isUploading && fileInput.current.click()}
                        disabled={isUploading}
                    >
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            {isUploading && (
                                <View style={{ marginRight: 6, animation: 'spin 1s linear infinite' }}>
                                    <Icon3
                                        name="MdRefresh"
                                        size={16}
                                        color="#192440"
                                    />
                                </View>
                            )}
                            <Text style={{ fontSize: 14, color: '#192440' }}>
                                {isUploading ? 'Processing...' : 'Import Excel'}
                            </Text>
                        </View>
                    </Pressable> */}
                    <Pressable
                        style={{
                            alignItems: 'center',
                            backgroundColor: '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        }}
                        onPress={() => !isUploading && fileInput.current.click()}
                        disabled={isUploading}
                    >
                        <Text style={{ color: '#334155', fontWeight: '500', fontSize: 14 }}>                                
                            {isUploading ? 'Processing...' : 'Upload Excel'}
                        </Text>
                    </Pressable>
                    <Pressable
                        style={{
                            alignItems: 'center',
                            backgroundColor: '#f8fafc',
                            padding: 12,
                            paddingHorizontal: 16,
                            borderRadius: 6,
                            flexDirection: 'row',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            marginLeft: 10,
                        }}
                        onPress={() => !isExporting && exportCOLogsToExcel()}
                        disabled={isExporting}
                    >
                        <Text style={{ color: '#334155', fontWeight: '500', fontSize: 14 }}>                                
                            {isExporting ? 'Exporting...' : 'Export Excel'}
                        </Text>
                    </Pressable>
                    <input
                        type="file"
                        ref={fileInput}
                        style={{ display: 'none', height: 0, width: 0 }}
                        onChange={fileSelectedHandler}
                        accept=".xlsx"
                    />
                </View>
            </View>

            <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, margin: 30, marginBottom: 100, flexDirection: 'row', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                <View>
                    {isUploading && (
                        <View style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1000,
                            borderRadius: 5,
                        }}>
                            <View style={{ alignItems: 'center' }}>
                                <View style={{ marginBottom: 10, animation: 'spin 1s linear infinite' }}>
                                    <Icon3 name="MdRefresh" size={32} color="#192440" />
                                </View>
                                <Text style={{ fontSize: 16, color: '#192440', fontWeight: '500' }}>
                                    Processing Excel file...
                                </Text>
                                <Text style={{ fontSize: 14, color: '#666', marginTop: 5 }}>
                                    Please wait while we import and save your data
                                </Text>
                            </View>
                        </View>
                    )}

                    <View style={{
                        borderWidth: bw,
                        borderColor: COLORS.grid,
                        backgroundColor: COLORS.white,
                        marginBottom: 20,
                        padding: 10,
                    }}>
                        <Text style={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            marginBottom: 10,
                            color: COLORS.navyDark,
                        }}>
                            Data Validation Summary
                        </Text>

                        {/* Table Header */}
                        <View style={{
                            flexDirection: "row",
                            borderBottomWidth: 2,
                            borderBottomColor: '#2563eb',
                            backgroundColor: '#f8fafc',
                        }}>
                            <Text style={{ width: 150, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 12, paddingHorizontal: 10 }}>Source</Text>
                            <Text style={{ width: 100, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 12, paddingHorizontal: 10, textAlign: 'right' }}>Revenue</Text>
                            <Text style={{ width: 100, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 12, paddingHorizontal: 10, textAlign: 'right' }}>Cost</Text>
                            <Text style={{ width: 100, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 12, paddingHorizontal: 10, textAlign: 'right' }}>Rows</Text>
                        </View>

                        {/* Upload Summary Row */}
                        <View style={{
                            flexDirection: "row",
                            borderBottomWidth: 1,
                            borderBottomColor: '#e5e7eb',
                            backgroundColor: '#ffffff',
                        }}>
                            <Text style={{ width: 150, fontSize: 13, padding: 10, paddingHorizontal: 10 }}>Upload Data</Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {fNum(coRows.filter(row => row.Cost_Type == 41000 || row.costType == 41000).reduce((sum, row) => sum + (parseFloat(row.Value_Amount || row.valueAmount) || 0), 0))}
                            </Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {fNum(coRows.filter(row => row.Cost_Type != 41000 && row.costType != 41000).reduce((sum, row) => sum + (parseFloat(row.Value_Amount || row.valueAmount) || 0), 0))}
                            </Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {coRows.filter(row => row.Forecast_Tab !== "Change Order Header").length}
                            </Text>
                        </View>

                        {/* Approved Summary Row */}
                        <View style={{
                            flexDirection: "row",
                            borderBottomWidth: 1,
                            borderBottomColor: '#e5e7eb',
                            backgroundColor: '#f0fdf4',
                        }}>
                            <Text style={{ width: 150, fontSize: 13, padding: 10, paddingHorizontal: 10 }}>Approved</Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {fNum(coRows.filter(row => (row.Status === 'Approved' || row.status === 'Approved') && (row.Cost_Type == 41000 || row.costType == 41000)).reduce((sum, row) => sum + (parseFloat(row.Value_Amount || row.valueAmount) || 0), 0))}
                            </Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {fNum(coRows.filter(row => (row.Status === 'Approved' || row.status === 'Approved') && (row.Cost_Type || row.costType || '').toString()[0] === '5' && (row.Cost_Code || row.costCode || '') !== '101101').reduce((sum, row) => sum + (parseFloat(row.Value_Amount || row.valueAmount) || 0), 0))}
                            </Text>
                            <Text style={{ width: 100, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>
                                {coRows.filter(row => (row.Status === 'Approved' || row.status === 'Approved') && row.Forecast_Tab !== "Change Order Header").length}
                            </Text>
                        </View>
                    </View>

                    {/* Approved CO Cost by Discipline */}
                    {(() => {
                        // Match overviewNew logic: only Cost_Type starting with '5', exclude contingency (101101)
                        const normCC = (cc) => (cc && /^\d{6}$/.test(cc)) ? cc : '100101';
                        const approvedCostRows = coRows.filter(row =>
                            (row.Status === 'Approved' || row.status === 'Approved') &&
                            row.Forecast_Tab !== 'Change Order Header' &&
                            row.tab !== 'Change Order Header' &&
                            (row.Cost_Type || row.costType || '').toString()[0] === '5' &&
                            (row.Cost_Code || row.costCode || '') !== '101101'
                        );

                        const byDiscipline = {};
                        approvedCostRows.forEach(row => {
                            const rawPhase = row.Cost_Code || row.costCode || '';
                            const phase = normCC(rawPhase);
                            const discipline = (phase && disciplines[phase]) ? disciplines[phase] : 'Unassigned';
                            if (!byDiscipline[discipline]) {
                                byDiscipline[discipline] = 0;
                            }
                            byDiscipline[discipline] += pNum(row.Value_Amount || row.valueAmount || 0);
                        });

                        const disciplineEntries = Object.entries(byDiscipline).sort((a, b) => a[0].localeCompare(b[0]));
                        const grandTotal = disciplineEntries.reduce((sum, [, val]) => sum + val, 0);

                        return (
                            <View style={{
                                borderWidth: bw,
                                borderColor: COLORS.grid,
                                backgroundColor: COLORS.white,
                                marginTop: 16,
                                marginBottom: 10,
                                padding: 10,
                            }}>
                                <Pressable onPress={() => setShowBreakout(prev => !prev)}>
                                    <Text style={{
                                        fontSize: 16,
                                        fontWeight: 'bold',
                                        marginBottom: 10,
                                        color: COLORS.navyDark,
                                    }}>
                                        {showBreakout ? '▼' : '▶'} Approved CO Cost by Discipline
                                    </Text>
                                </Pressable>

                                {showBreakout && <>
                                <View style={{
                                    flexDirection: 'row',
                                    borderBottomWidth: 2,
                                    borderBottomColor: '#2563eb',
                                    backgroundColor: '#f8fafc',
                                }}>
                                    <Text style={{ width: 220, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 10, paddingHorizontal: 10 }}>Discipline</Text>
                                    <Text style={{ width: 140, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>Approved Cost</Text>
                                </View>

                                {disciplineEntries.length === 0 ? (
                                    <View style={{ padding: 12 }}>
                                        <Text style={{ color: '#64748b', fontSize: 13 }}>No approved cost rows found. Assign disciplines in the Productivity Tracker.</Text>
                                    </View>
                                ) : (
                                    disciplineEntries.map(([disc, total], i) => (
                                        <View key={i} style={{
                                            flexDirection: 'row',
                                            borderBottomWidth: 1,
                                            borderBottomColor: '#e5e7eb',
                                            backgroundColor: i % 2 === 0 ? '#ffffff' : COLORS.zebra,
                                        }}>
                                            <Text style={{ width: 220, fontSize: 13, padding: 10, paddingHorizontal: 10 }}>{disc}</Text>
                                            <Text style={{ width: 140, fontSize: 13, padding: 10, paddingHorizontal: 10, textAlign: 'right' }}>{fNum(total)}</Text>
                                        </View>
                                    ))
                                )}

                                {disciplineEntries.length > 0 && (
                                    <View style={{
                                        flexDirection: 'row',
                                        borderTopWidth: 2,
                                        borderTopColor: '#2563eb',
                                        backgroundColor: '#eff6ff',
                                    }}>
                                        <Text style={{ width: 220, fontSize: 13, fontWeight: '700', padding: 10, paddingHorizontal: 10, color: COLORS.navyDark }}>Total</Text>
                                        <Text style={{ width: 140, fontSize: 13, fontWeight: '700', padding: 10, paddingHorizontal: 10, textAlign: 'right', color: COLORS.navyDark }}>{fNum(grandTotal)}</Text>
                                    </View>
                                )}
                                </>}
                            </View>
                        );
                    })()}

                    {/* LEMSO Totals */}
                    {(() => {
                        const approvedRows = coRows.filter(row =>
                            (row.Status === 'Approved' || row.status === 'Approved') &&
                            row.Forecast_Tab !== 'Change Order Header' &&
                            row.tab !== 'Change Order Header' &&
                            (row.Cost_Code || row.costCode || '') !== '101101'
                        );
                        const lemso = { Revenue: 0, Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                        const lemsoHrs = { Revenue: 0, Labor: 0, Equipment: 0, Materials: 0, Subs: 0, Other: 0 };
                        approvedRows.forEach(r => {
                            const ct = (r.Cost_Type || r.costType || '').toString();
                            const val = pNum(r.Value_Amount || r.valueAmount || 0);
                            const hrs = parseFloat(r.Hours || r.hours || 0) || 0;
                            if (ct >= '41000' && ct <= '49999') { lemso.Revenue += val; }
                            else if (ct >= '51000' && ct <= '51999') { lemso.Labor += val; lemsoHrs.Labor += hrs; }
                            else if (ct >= '52000' && ct <= '52999') { lemso.Equipment += val; lemsoHrs.Equipment += hrs; }
                            else if (ct >= '53000' && ct <= '53999') { lemso.Materials += val; }
                            else if (ct >= '54000' && ct <= '54999') { lemso.Subs += val; }
                            else if (ct >= '55000' && ct <= '59999') { lemso.Other += val; }
                        });
                        const costTotal = lemso.Labor + lemso.Equipment + lemso.Materials + lemso.Subs + lemso.Other;
                        const entries = [
                            ['Revenue', lemso.Revenue, 0],
                            ['Labor', lemso.Labor, lemsoHrs.Labor],
                            ['Equipment', lemso.Equipment, lemsoHrs.Equipment],
                            ['Materials', lemso.Materials, 0],
                            ['Subs', lemso.Subs, 0],
                            ['Other', lemso.Other, 0],
                        ];
                        return (
                            <View style={{ borderWidth: bw, borderColor: COLORS.grid, backgroundColor: COLORS.white, marginTop: 16, marginBottom: 10, padding: 10 }}>
                                <Pressable onPress={() => setShowBreakout(prev => !prev)}>
                                    <Text style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 10, color: COLORS.navyDark }}>{showBreakout ? '▼' : '▶'} Approved CO LEMSO Totals</Text>
                                </Pressable>
                                {showBreakout && <>
                                <View style={{ flexDirection: 'row', borderBottomWidth: 2, borderBottomColor: '#2563eb', backgroundColor: '#f8fafc' }}>
                                    <Text style={{ width: 180, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 10 }}>Category</Text>
                                    <Text style={{ width: 140, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 10, textAlign: 'right' }}>Amount</Text>
                                    <Text style={{ width: 100, color: '#1e293b', fontWeight: '600', fontSize: 13, padding: 10, textAlign: 'right' }}>Hours</Text>
                                </View>
                                {entries.map(([cat, val, hrs], i) => (
                                    <View key={cat} style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#e5e7eb', backgroundColor: i % 2 === 0 ? '#ffffff' : COLORS.zebra }}>
                                        <Text style={{ width: 180, fontSize: 13, padding: 10 }}>{cat}</Text>
                                        <Text style={{ width: 140, fontSize: 13, padding: 10, textAlign: 'right' }}>{fNum(val, 1)}</Text>
                                        <Text style={{ width: 100, fontSize: 13, padding: 10, textAlign: 'right' }}>{hrs > 0 ? fNum(hrs) : '-'}</Text>
                                    </View>
                                ))}
                                <View style={{ flexDirection: 'row', borderTopWidth: 2, borderTopColor: '#2563eb', backgroundColor: '#eff6ff' }}>
                                    <Text style={{ width: 180, fontSize: 13, fontWeight: '700', padding: 10, color: COLORS.navyDark }}>Total Costs</Text>
                                    <Text style={{ width: 140, fontSize: 13, fontWeight: '700', padding: 10, textAlign: 'right', color: COLORS.navyDark }}>{fNum(costTotal, 1)}</Text>
                                    <Text style={{ width: 100, fontSize: 13, padding: 10 }}></Text>
                                </View>
                                <View style={{ flexDirection: 'row', backgroundColor: '#dbeafe' }}>
                                    <Text style={{ width: 180, fontSize: 13, fontWeight: '700', padding: 10, color: COLORS.navyDark }}>Gross Profit</Text>
                                    <Text style={{ width: 140, fontSize: 13, fontWeight: '700', padding: 10, textAlign: 'right', color: lemso.Revenue - costTotal >= 0 ? '#16a34a' : '#dc2626' }}>{fNum(lemso.Revenue - costTotal, 1)}</Text>
                                    <Text style={{ width: 100, fontSize: 13, padding: 10 }}></Text>
                                </View>
                                </>}
                            </View>
                        );
                    })()}

                    <View style={{
                        borderWidth: bw,
                        borderColor: COLORS.grid,
                        backgroundColor: COLORS.white,
                        marginTop: 10,
                    }}>
                        <Text style={{
                            fontSize: 16,
                            fontWeight: 'bold',
                            margin: 10,
                            color: COLORS.navyDark,
                        }}>
                            Uploaded Data ({coRows.filter(row => row.tab !== "Change Order Header" && row.Forecast_Tab !== "Change Order Header").length} rows)
                        </Text>

                        {/* Table Header */}
                        <View style={{
                            flexDirection: "row",
                            borderBottomWidth: 2,
                            borderBottomColor: '#2563eb',
                            backgroundColor: '#f8fafc',
                        }}>
                            {[
                                { label: "CO ID", width: 70 },
                                { label: "Description", width: 200 },
                                { label: "Status", width: 100 },
                                { label: "Cost Code", width: 200 },
                                { label: "Cost Type", width: 200 },
                                { label: "Dollars", width: 120 },
                                { label: "Hours", width: 80 },
                                { label: "Quantity", width: 80 },
                                { label: "Resource Code", width: 150 },
                            ].map((h, i) => (
                                <View
                                    key={`header-${i}`}
                                    style={{
                                        width: h.width,
                                        justifyContent: "center",
                                    }}
                                >
                                    <Text style={{
                                        color: '#1e293b',
                                        fontWeight: "600",
                                        fontSize: 13,
                                        textAlign: h.label === 'CO ID' || h.label === 'Description' || h.label === 'Status' || h.label === 'Cost Code' || h.label === 'Cost Type' || h.label === 'Resource Code' ? 'left' : 'right',
                                    }}>
                                        {h.label}
                                    </Text>
                                </View>
                            ))}
                        </View>

                        {/* Data Rows */}
                        <ScrollView horizontal>
                            <View>
                                {coRows
                                    //.filter(row => row.tab == "Change Order")
                                    .sort((a, b) => {
                                        const aId = String(a.Row_Label || a.rowLabel || '');
                                        const bId = String(b.Row_Label || b.rowLabel || '');
                                        
                                        // Handle empty strings
                                        if (!aId) return 1;
                                        if (!bId) return -1;
                                        
                                        return aId.localeCompare(bId, undefined, { numeric: true });
                                    })
                                    .map((row, index) => (
                                    <View
                                        key={`row-${index}`}
                                        style={{
                                            flexDirection: "row",
                                            borderBottomWidth: 1,
                                            borderBottomColor: '#e5e7eb',
                                            backgroundColor: '#ffffff',
                                        }}
                                    >
                                        <View style={{ width: 70, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {row.Row_Label || row.rowLabel || ''}
                                            </Text>
                                        </View>
                                        <View style={{ width: 200, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {row.description || row.Description || ''}
                                            </Text>
                                        </View>
                                        <View style={{ width: 100, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {row.Status || row.status || ''}
                                            </Text>
                                        </View>
                                        <View style={{ width: 200, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {(() => {
                                                    const code = row.Cost_Code || row.costCode || '';
                                                    if (!code) return '';
                                                    
                                                    // Try to get description from jobPhaseCodes first, then fall back to stored description
                                                    const desc = (jobPhaseCodes && jobPhaseCodes[code]) || row.Cost_Code_Description || row.costCodeDescription || '';
                                                    
                                                    return desc ? `${code} - ${desc}` : code;
                                                })()}
                                            </Text>
                                        </View>
                                        <View style={{ width: 200, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {(() => {
                                                    const type = row.Cost_Type || row.costType || '';
                                                    if (!type) return '';
                                                    const desc = costTypeDescriptions[type] || '';
                                                    return desc ? `${type} - ${desc}` : type;
                                                })()}
                                            </Text>
                                        </View>
                                        <View style={{ width: 120, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13, textAlign: 'right' }}>
                                                {fNum(row.Value_Amount || row.valueAmount || 0)}
                                            </Text>
                                        </View>
                                        <View style={{ width: 80, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13, textAlign: 'right' }}>
                                                {row.Hours || row.hours || ''}
                                            </Text>
                                        </View>
                                        <View style={{ width: 80, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13, textAlign: 'right' }}>
                                                {row.Quantity || row.quantity || ''}
                                            </Text>
                                        </View>
                                        <View style={{ width: 150, paddingVertical: 10, paddingHorizontal: 10 }}>
                                            <Text numberOfLines={1} style={{ color: '#1e293b', fontSize: 13 }}>
                                                {row.Resource_Code || row.resourceCode || ''}
                                            </Text>
                                        </View>
                                    </View>
                                ))}
                            </View>
                        </ScrollView>
                    </View>
                </View>
            </View>
        </View>
    );
};

export default COLogUpload;